import { useId } from "react";
import { Label, Select as SelectRadix } from "radix-ui";
import { ChevronDown, Check } from "lucide-react";
import styles from "./Select.css";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  name: string;
  value?: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  label: string;
  /** Id of the element describing this select — normally its error message. */
  "aria-describedby"?: string;
}

const Select = ({
  name,
  value,
  onValueChange,
  options,
  placeholder,
  required,
  disabled,
  error,
  label,
  "aria-describedby": ariaDescribedBy,
}: SelectProps) => {
  const triggerId = useId();
  const labelId = useId();

  return (
    <div className={styles.field}>
      <Label.Root id={labelId} htmlFor={triggerId}>
        {label} {required && "*"}
      </Label.Root>
      <SelectRadix.Root
        name={name}
        value={value}
        onValueChange={onValueChange}
        required={required}
        disabled={disabled}
      >
        <SelectRadix.Trigger
          id={triggerId}
          className={styles.trigger({ error })}
          aria-labelledby={labelId}
          aria-invalid={error}
          aria-describedby={ariaDescribedBy}
        >
          <span className={styles.value}>
            <SelectRadix.Value placeholder={placeholder} />
          </span>
          <SelectRadix.Icon className={styles.icon}>
            <ChevronDown size={16} />
          </SelectRadix.Icon>
        </SelectRadix.Trigger>

        <SelectRadix.Portal>
          <SelectRadix.Content
            className={styles.content}
            position="popper"
            sideOffset={4}
          >
            <SelectRadix.Viewport className={styles.viewport}>
              {options.map((option) => (
                <SelectRadix.Item
                  key={option.value}
                  value={option.value}
                  className={styles.item}
                >
                  <SelectRadix.ItemText>{option.label}</SelectRadix.ItemText>
                  <SelectRadix.ItemIndicator className={styles.indicator}>
                    <Check size={16} />
                  </SelectRadix.ItemIndicator>
                </SelectRadix.Item>
              ))}
            </SelectRadix.Viewport>
          </SelectRadix.Content>
        </SelectRadix.Portal>
      </SelectRadix.Root>
    </div>
  );
};

export default Select;
